import {useIntl} from 'react-intl';
import CardComponent from '../../../common/components/CardComponent';
import {DetailedOrder} from '../../../interfaces/order';
import {formatDate} from '../../../common/tools/date';

interface DetailsBlocProps extends DetailedOrder {
    blocTitle: string;
}

const DetailsBloc = ({
    blocTitle,
    description,
    rate,
    deposit,
    grt,
    commitment,
    mesExpectedDate,
    mesContractDate,
}: DetailsBlocProps): JSX.Element => {
    const intl = useIntl();

    const highlights: {label: string; value: string}[] = [];
    if (rate !== null) {
        highlights.push({
            label: intl.formatMessage({id: 'ORDERS.DETAILS.PRICE'}),
            value: `${Number(rate).toFixed(2)}€`,
        });
    }
    if (commitment !== null) {
        highlights.push({
            label: intl.formatMessage({id: 'ORDERS.DETAILS.COMMITMENT'}),
            value: `${commitment}${intl.formatMessage({id: 'ORDERS.DETAILS.MONTHS_SUFFIX'})}`,
        });
    }

    const items: {label: string; value: string}[] = [];
    if (description !== null) {
        items.push({label: intl.formatMessage({id: 'COMMON.DESCRIPTION'}), value: description});
    }
    if (deposit !== null) {
        items.push({label: intl.formatMessage({id: 'ORDERS.DETAILS.DEPOSIT'}), value: `${deposit}€`});
    }
    if (grt !== null && grt !== '') {
        items.push({label: intl.formatMessage({id: 'ORDERS.DETAILS.GRT'}), value: grt});
    }
    if (mesExpectedDate !== null) {
        items.push({
            label: intl.formatMessage({id: 'ORDERS.DETAILS.PLANNED_MES'}),
            value: formatDate(mesExpectedDate),
        });
    }
    if (mesContractDate !== null) {
        items.push({
            label: intl.formatMessage({id: 'ORDERS.DETAILS.CONTRACTUAL_MES'}),
            value: formatDate(mesContractDate),
        });
    }

    return (
        <CardComponent title={blocTitle}>
            <div className="d-flex flex-column flex-md-row gap-6">
                {highlights.length > 0 && (
                    <div
                        className="bg-celeste-primary text-white rounded p-6 text-center flex-shrink-0"
                        style={{minWidth: '160px'}}
                    >
                        {highlights.map((highlight) => (
                            <div className="mb-2" key={highlight.label}>
                                <div className="fs-3 fw-bold">{highlight.value}</div>
                                <div className="fs-8 opacity-75 text-uppercase">
                                    {highlight.label}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex-grow-1">
                    {items.map((item, index) => (
                        <div
                            className={`d-flex justify-content-between py-2 ${
                                index !== items.length - 1 ? 'border-bottom border-gray-300' : ''
                            }`}
                            key={item.label}
                        >
                            <span className="fw-semibold text-muted">{item.label}</span>
                            <span className="fw-bold text-gray-800">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </CardComponent>
    );
};

export default DetailsBloc;
